import { Component, OnInit, inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UserService, UserData } from '../services/user.service';
import { Address } from '../models/address.model';
import { AuthService } from '../services/auth.service';
import { Order, Product } from '../models/admin.models';
import { firstValueFrom } from 'rxjs';
import { ReviewPopupComponent } from '../shared/review-popup/review-popup.component';
import { ToastService } from '../services/toast.service';
import { NavbarComponent } from '../shared/navbar/navbar.component';

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ReviewPopupComponent, NavbarComponent],
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss']
})
export class UserProfileComponent implements OnInit {
  private userService = inject(UserService);
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);
  private toastService = inject(ToastService);

  activeTab: 'profile' | 'orders' | 'addresses' = 'profile';
  isDesktop: boolean = true;
  
  user: UserData | null | undefined = undefined;
  orders: Order[] = [];
  
  profileForm: FormGroup;
  addressForm: FormGroup;
  
  isEditingAddress = false;
  currentAddressId: string | null = null;
  showAddressForm = false;

  selectedProductForReview: Product | null = null;
  showReviewPopup = false;

  constructor() {
    this.profileForm = this.fb.group({
      displayName: ['', Validators.required],
      email: [{value: '', disabled: true}],
      countryCode: ['+91'],
      phoneNumber: ['']
    });

    this.addressForm = this.fb.group({
      type: ['Home', Validators.required],
      receiverName: ['', Validators.required],
      countryCode: ['+91', Validators.required],
      mobileNumber: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]],
      street: ['', Validators.required],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zipCode: ['', [Validators.required, Validators.pattern('^[0-9]+$')]],
      isDefault: [false]
    });
  }

  async ngOnInit() {
    this.checkBreakpoint();
    this.authService.userProfile$.subscribe(profile => {
      this.user = profile;
      if (profile) {
        let phone = profile.phoneNumber || '';
        let code = '+91';
        if (phone.includes(' ')) {
           const parts = phone.split(' ');
           code = parts[0];
           phone = parts[1];
        }

        this.profileForm.patchValue({
          displayName: profile.displayName,
          email: profile.email,
          countryCode: code,
          phoneNumber: phone
        });
        
        this.loadOrders(profile.uid);
      }
    });
  }

  @HostListener('window:resize')
  onResize() {
    this.checkBreakpoint();
  }

  private checkBreakpoint() {
    this.isDesktop = window.innerWidth >= 768;
  }

  setActiveTab(tab: 'profile' | 'orders' | 'addresses') {
    this.activeTab = tab;
  }

  async updateProfile() {
    if (this.profileForm.valid && this.user) {
      try {
        const countryCode = this.profileForm.get('countryCode')?.value;
        const phone = this.profileForm.get('phoneNumber')?.value;
        
        const updatedData = {
          displayName: this.profileForm.get('displayName')?.value,
          phoneNumber: `${countryCode} ${phone}`
        };
        await firstValueFrom(this.userService.updateProfile(this.user.uid, updatedData));
        await this.authService.refreshUserProfile();
        this.toastService.success('Profile updated successfully!');
      } catch (error) {
        console.error('Error updating profile:', error);
        this.toastService.error('Failed to update profile.');
      }
    }
  }

  initAddAddress() {
    this.isEditingAddress = false;
    this.currentAddressId = null;
    
    let phone = '';
    let code = '+91';
    if (this.user?.phoneNumber) {
      phone = this.user.phoneNumber;
      if (phone.includes(' ')) {
        const parts = phone.split(' ');
        code = parts[0];
        phone = parts[1];
      } else if (phone.length > 10) {
        code = phone.substring(0, phone.length - 10);
        phone = phone.substring(phone.length - 10);
      }
    }

    this.addressForm.reset({ 
      type: 'Home', 
      isDefault: false,
      countryCode: code,
      mobileNumber: phone,
      receiverName: this.user?.displayName || ''
    });
    this.showAddressForm = true;
  }

  editAddress(address: Address) {
    this.isEditingAddress = true;
    this.currentAddressId = address.id;
    this.addressForm.patchValue(address);
    this.showAddressForm = true;
  }

  cancelAddressEdit() {
    this.showAddressForm = false;
  }

  async saveAddress() {
    if (this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      return;
    }
    
    if (this.user) {
      const formVal = this.addressForm.value;
      const addressData: Address = {
        ...formVal,
        id: this.currentAddressId || crypto.randomUUID()
      };

      try {
        if (this.isEditingAddress && this.currentAddressId) {
          await firstValueFrom(this.userService.updateAddress(this.user.uid, this.currentAddressId, addressData));
        } else {
          await firstValueFrom(this.userService.addAddress(this.user.uid, addressData));
        }
        await this.authService.refreshUserProfile();
        this.showAddressForm = false;
        this.toastService.success('Address saved successfully!');
      } catch (error) {
        console.error('Error saving address:', error);
        this.toastService.error('Failed to save address.');
      }
    }
  }

  getFormValidationErrors() {
    const errors: any = {};
    Object.keys(this.addressForm.controls).forEach(key => {
      const control = this.addressForm.get(key);
      if (control && control.invalid) {
        errors[key] = control.errors;
      }
    });
    return errors;
  }

  async deleteAddress(index: number) {
    if (confirm('Are you sure you want to delete this address?') && this.user) {
      try {
        await firstValueFrom(this.userService.deleteAddress(this.user.uid, index));
        await this.authService.refreshUserProfile();
        this.toastService.success('Address deleted successfully.');
      } catch (error) {
        console.error('Error deleting address:', error);
        this.toastService.error('Failed to delete address.');
      }
    }
  }

  async loadOrders(uid: string) {
    try {
      const orders = await firstValueFrom(this.userService.getOrders());
      this.orders = orders || [];
    } catch (error: any) {
      console.error('Error loading orders:', error);
    }
  }
  
  getInitials(name: string | null): string {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  }

  openReviewPopup(product: Product) {
    this.selectedProductForReview = product;
    this.showReviewPopup = true;
  }

  closeReviewPopup() {
    this.showReviewPopup = false;
    this.selectedProductForReview = null;
  }

  onReviewSubmitted(res: any) {
    this.toastService.success('Thank you for your review! It has been submitted for moderation.');
    this.closeReviewPopup();
  }
}
